import Text from '@creator/ui/components/Text/Text';
import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import Media, { type MediaProps } from '@src/basic-components/Media/Media';
import ResponseFullScreenButton from '@src/basic-components/ResponseFullScreenButton/ResponseFullScreenButton';
import { getMediaPoster } from '@src/model/upvote/helpers';
import { cleanHtmlToPlainText } from '@src/utils/html-utils/html-utils';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import type { FC } from 'react';
import { memo } from 'react';

type VideoToolFile = {
    url: string;
};
type BlockData = {
    url: string;
    withBorder: boolean;
    stretched: boolean;
    file: VideoToolFile;
    caption: string;
};

export interface EditorViewOnlyVideoToolProps {
    postId: string;
    className?: string;
    blockData: BlockData;
    tokenName?: string;
    mediaPreivewProps?: Partial<MediaProps>;
    showFullScreenButton?: boolean;
    mediaProps?: Partial<MediaProps>;
}

const EditorViewOnlyVideoTool: FC<EditorViewOnlyVideoToolProps> = props => {
    const { blockData, className, tokenName } = props;

    function renderMedia() {
        const { mediaProps = {} } = props;

        const { url, image, cdn } = blockData;

        const _props = mergeProps<[MediaProps, Partial<MediaProps>]>(
            {
                poster: image ? image.url : getMediaPoster(url),
                src: cdn?.mp4Url ? cdn.mp4Url : url,
                type: 'video',
            },
            mediaProps
        );

        return <Media {..._props} />;
    }

    function renderCaption() {
        const { caption } = blockData;
        if (!caption) return;
        return (
            <Text enableMultiline={true} className="block mt-1 text-sm text-gray-500" data-placeholder="Caption">
                {cleanHtmlToPlainText(caption)}
            </Text>
        );
    }

    function renderContent() {
        const { stretched, withBorder } = blockData;

        const withBorderClassName = withBorder ? 'video-tool--withBorder' : '';
        const stretchedClassName = stretched ? 'video-tool--stretched' : '';

        return (
            <div className={`cdx-block video-tool video-tool--filled ${withBorderClassName} ${stretchedClassName}`}>
                <div id="video-container" className="video-tool__video ">
                    <div id="video-preloader" className="pointer-events-none video-tool__video-preloader -z-50" />
                    {renderMedia()}
                </div>
                {renderCaption()}
            </div>
        );
    }

    function renderResponseFullScreenButton() {
        const { showFullScreenButton, postId } = props;
        if (!showFullScreenButton) return;

        return <ResponseFullScreenButton className="absolute z-10 right-4 top-4" postId={postId} />;
    }

    return (
        <div className={`relative video-tool ${className}`}>
            {renderContent()}
            {renderResponseFullScreenButton()}
        </div>
    );
};

export default memo(EditorViewOnlyVideoTool, propsEqualityFn);
